/**
 * Inside a workspace: its files, its people, its chat.
 *
 * The design never drew this screen — the prototype has workspace *cards* with
 * a "3 new messages" pill and nothing behind them — so the layout is
 * originated. It reuses the file browser wholesale rather than growing a second
 * one: `Files` takes a `root`, and a workspace is a root. That is the payoff
 * from making a workspace a node rather than a parallel concept.
 *
 * Members are shown with their key state, because "invited" and "can actually
 * read this" are genuinely different in an end-to-end encrypted product and the
 * UI has to be honest about which one someone is in.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { errorText, type WorkspaceRole } from '../api';
import { ds } from '../ds';
import { useRouter } from '../router';
import { Footnote, Panel } from '../components/Shell';
import { Projects } from '../components/Projects';
import { Chat } from '../components/Chat';
import { Files } from './Files';
import { api } from '../api';
import { enterWorkspace, inviteMember, type WorkspaceView,
  workspaceChannel
} from '../workspaces';
import { useIsNarrow } from '../responsive';
import { formatBytes, monoPlain } from '../theme';
import { useT } from '../i18n';

const ROLES: WorkspaceRole[] = ['editor', 'commenter', 'viewer'];

export function Workspace({ workspaceId }: { workspaceId: string }) {
  const t = useT();
  const { Button, Icon, Input } = ds();
  const { navigate } = useRouter();
  const narrow = useIsNarrow();

  const [view, setView] = useState<WorkspaceView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<WorkspaceRole>('editor');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setView(await enterWorkspace(workspaceId));
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Could not open that workspace.'));
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const invite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const { pending } = await inviteMember(workspaceId, email.trim(), role);
      setNotice(
        pending
          ? `${email.trim()} has no vault yet. They are invited, and their key is wrapped the moment they set one up.`
          : `${email.trim()} can open this workspace now.`
      );
      setEmail('');
      setInviting(false);
      await load();
    } catch (err) {
      setError(errorText(err, 'Could not send that invitation.'));
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (memberId: string, label: string) => {
    setBusy(true);
    try {
      await api.workspaces.removeMember(workspaceId, memberId);
      setNotice(
        `${label} was removed.`
      );
      await load();
    } catch (err) {
      setError(errorText(err, 'Could not remove that member.'));
    } finally {
      setBusy(false);
    }
  };

  const destroy = async () => {
    setBusy(true);
    try {
      await api.workspaces.remove(workspaceId);
      navigate('/workspaces');
    } catch (err) {
      setError(errorText(err, 'Could not delete that workspace.'));
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Panel style={{ padding: '40px 34px' }}>
        <span style={monoPlain}>{t('workspace.loading')}</span>
      </Panel>
    );
  }

  if (!view) {
    return (
      <Panel style={{ padding: '40px 34px', display: 'grid', gap: 8 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-strong)' }}>
          {error ?? 'That workspace is not available.'}
        </span>
        <span style={monoPlain}>{t('workspace.it-may-have-been-deleted')}</span>
      </Panel>
    );
  }

  const { workspace, members, key } = view;
  const canPost = workspace.role !== 'viewer';

  return (
    <div style={{ display: 'grid', gap: 22 }}>
      {error && (
        <Panel style={{ padding: '14px 20px', background: 'var(--danger-100)' }}>
          <span style={{ fontSize: 13.5, color: 'var(--danger-600)' }}>{error}</span>
        </Panel>
      )}
      {notice && (
        <Panel style={{ padding: '14px 20px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <Icon name="info" size={16} />
          <span style={{ flex: 1, fontSize: 13.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
            {notice}
          </span>
          <button
            onClick={() => setNotice(null)}
            aria-label={t('workspace.dismiss')}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
          >
            <Icon name="x" size={15} />
          </button>
        </Panel>
      )}

      <div
        style={{
          display: 'grid',
          gap: 22,
          // The chat is a rail beside the files on a desktop and a stacked
          // section below them on a phone — a 320px column in 360px of viewport
          // leaves nothing for the file list.
          gridTemplateColumns: narrow ? '1fr' : 'minmax(0, 1fr) 340px',
          alignItems: 'start'
        }}
      >
        <div style={{ display: 'grid', gap: 22, minWidth: 0 }}>
          <Files
            root={{
              id: workspace.rootNodeId,
              label: workspace.name,
              href: `/workspaces/${workspace.id}`
            }}
          />
        </div>

        <div style={{ display: 'grid', gap: 18, minWidth: 0 }}>
          <Panel style={{ padding: '18px 20px', display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="hard-drive" size={16} />
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-strong)' }}>
                {t('workspace.workspace-storage')}
              </span>
            </div>
            <span style={monoPlain}>
              {formatBytes(view.storage.usedBytes)} stored
            </span>
            <span style={monoPlain}>
              {formatBytes(view.pool.availableBytes)} left of{' '}
              {formatBytes(view.pool.limitBytes)} in the pool · {view.pool.percent}%
            </span>
          </Panel>

          <Projects workspaceId={workspace.id} canCreate={workspace.isOwner} />

          <Panel style={{ padding: '18px 20px', display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="users" size={16} />
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-strong)' }}>
                {t('workspace.members')}
              </span>
              <span style={{ ...monoPlain, marginLeft: 'auto' }}>{members.length}</span>
            </div>

            <div style={{ display: 'grid', gap: 9 }}>
              {members.map((m) => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: 'var(--text-strong)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {m.name}
                    </div>
                    <div style={monoPlain}>
                      {m.role}
                      {/* Invited is not the same as able to read. Say which. */}
                      {m.awaitingKey ? ' · waiting for their vault' : ''}
                    </div>
                  </div>
                  {workspace.isOwner && m.role !== 'owner' && (
                    <button
                      onClick={() => void removeMember(m.id, m.name)}
                      disabled={busy}
                      aria-label={`Remove ${m.name}`}
                      style={{
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: 'var(--text-muted)'
                      }}
                    >
                      <Icon name="x" size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {workspace.isOwner &&
              (inviting ? (
                <form onSubmit={invite} style={{ display: 'grid', gap: 10 }}>
                  <Input
                    label={t('workspace.email')}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
                    placeholder={t('workspace.teammateexamplecom')}
                  />
                  <div style={{ display: 'flex', gap: 6 }}>
                    {ROLES.map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setRole(r)}
                        style={{
                          flex: 1,
                          padding: '6px 8px',
                          borderRadius: 8,
                          fontSize: 12.5,
                          cursor: 'pointer',
                          textTransform: 'capitalize',
                          border: '1px solid var(--border-subtle)',
                          background: role === r ? 'var(--accent-100)' : 'transparent',
                          color: role === r ? 'var(--accent-600)' : 'var(--text-muted)'
                        }}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button variant="primary" size="sm" type="submit" disabled={busy || !email.trim()}>
                      {busy ? 'Inviting…' : 'Invite'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setInviting(false)}>
                      {t('workspace.cancel')}
                    </Button>
                  </div>
                </form>
              ) : (
                <Button variant="secondary" size="sm" fullWidth onClick={() => setInviting(true)}>
                  {t('workspace.invite-someone')}
                </Button>
              ))}
          </Panel>

          <div style={{ height: narrow ? 420 : 460 }}>
            <Chat
              workspaceId={workspace.id}
              channel={workspaceChannel(key)}
              members={members}
              canPost={canPost}
            />
          </div>

          {workspace.isOwner && (
            <Panel style={{ padding: '18px 20px', display: 'grid', gap: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-strong)' }}>
                {t('workspace.delete-this-workspace')}
              </span>
              <Footnote>
                {t('workspace.every-file-in-it-is')}
              </Footnote>
              <div>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm(`Delete “${workspace.name}” and everything in it?`)) {
                      void destroy();
                    }
                  }}
                >
                  {t('workspace.delete-workspace')}
                </Button>
              </div>
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}
